import React, { useState, useEffect } from 'react';
import DicomCanvas from './DicomCanvas.jsx';
import { getStudy, downloadDicom } from '../api/client.js';
import { parseDICOM } from '../lib/dicom-parser.js';
import styles from './ComparisonView.module.css';

// Prior-study comparison. Loads a secondary study alongside the primary.
// W/L is linked unless the user toggles "unlink".
export default function ComparisonView({ primaryUid, onClose }) {
  const [secondaryUid, setSecondaryUid] = useState('');
  const [primary, setPrimary] = useState(null);
  const [secondary, setSecondary] = useState(null);
  const [linked, setLinked] = useState(true);
  const [wc, setWc] = useState(400);
  const [ww, setWw] = useState(1800);
  const [wcB, setWcB] = useState(400);
  const [wwB, setWwB] = useState(1800);

  useEffect(() => { loadOne(primaryUid, setPrimary); }, [primaryUid]);

  async function loadSecondary() {
    if (!secondaryUid) return;
    await loadOne(secondaryUid, setSecondary);
  }

  async function loadOne(uid, set) {
    try {
      const r = await getStudy(uid);
      const img = r.images?.[0];
      if (!img?.sasUrl) return set(null);
      const buf = await downloadDicom(img.sasUrl);
      const parsed = parseDICOM(buf);
      set({ study: r.study, parsed });
      if (Number.isFinite(parsed.windowCenter) && Number.isFinite(parsed.windowWidth)) {
        if (set === setPrimary) { setWc(parsed.windowCenter); setWw(parsed.windowWidth); }
        else                    { setWcB(parsed.windowCenter); setWwB(parsed.windowWidth); }
      }
    } catch (err) { set(null); }
  }

  const applyWindow = (newWc, newWw, side) => {
    if (linked) { setWc(newWc); setWw(newWw); setWcB(newWc); setWwB(newWw); }
    else if (side === 'A') { setWc(newWc); setWw(newWw); }
    else { setWcB(newWc); setWwB(newWw); }
  };

  return (
    <div className={styles.shell}>
      <div className={styles.head}>
        <div className={styles.title}>Compare studies</div>
        <div className={styles.controls}>
          <label className={styles.cb}>
            <input type="checkbox" checked={linked} onChange={(e) => setLinked(e.target.checked)} /> Link W/L
          </label>
          <input
            className={styles.uidInput}
            placeholder="Prior Study Instance UID"
            value={secondaryUid}
            onChange={(e) => setSecondaryUid(e.target.value)}
          />
          <button className={styles.btn} onClick={loadSecondary}>Load</button>
          <button className={`${styles.btn} ${styles.close}`} onClick={onClose}>Close</button>
        </div>
      </div>
      <div className={styles.panes}>
        <Pane label="Current" data={primary}  wc={wc}  ww={ww}  onWindow={(w, d) => applyWindow(w, d, 'A')} />
        <Pane label="Prior"   data={secondary} wc={wcB} ww={wwB} onWindow={(w, d) => applyWindow(w, d, 'B')} />
      </div>
    </div>
  );
}

function Pane({ label, data, wc, ww, onWindow }) {
  return (
    <div className={styles.pane}>
      <div className={styles.paneHead}>
        <strong>{label}</strong>
        {data?.study ? (
          <span className={styles.meta}>
            {data.study.study_date || ''} · {data.study.modality || ''} · {data.study.body_part || ''}
          </span>
        ) : <span className={styles.meta}>— no study loaded —</span>}
      </div>
      <div className={styles.canvasSlot}>
        {data?.parsed ? (
          <DicomCanvas
            parsed={data.parsed}
            tool="wl"
            wc={wc} ww={ww}
            onWindowChange={onWindow}
            overlays={{
              topLeft:    data.study?.patient_name || '',
              topRight:   `WC ${Math.round(wc)}\nWW ${Math.round(ww)}`,
              bottomLeft: `${data.parsed.cols}×${data.parsed.rows}`,
            }}
          />
        ) : (
          <div className={styles.empty}>Load a study to compare.</div>
        )}
      </div>
    </div>
  );
}
